/**
 * ReadbackField — a READ-ONLY value inside FormField's measured box.
 *
 * WHY THIS EXISTS. P4 ("Review your writeup") reads back everything the builder typed
 * on P2/P3. The Design draws those boxes EMPTY, because the frame is a static capture
 * of an unfilled form — its own TODO says "wire all of them to the wizard's collected
 * state (read-only on this review step)". So the box is the Design's; only the value
 * inside it is new, and it must not be editable: P4 is a review, and the editable copy
 * of every one of these fields is two steps back.
 *
 * WHY NOT `<FormField value=… />`. That renders a controlled `<input>`, and a
 * controlled input with no `onChange` is exactly the case React warns about — it also
 * LOOKS editable, invites a keystroke, and silently discards it. Passing `readOnly`
 * through FormField would mean adding a prop that changes what its existing JSX
 * renders, which the port's rule forbids (additive props only). FormField already has
 * the right seam: its `children` slot renders "instead of an input, for read-only
 * review rows / custom controls" — this is that control, and P4 passes it as children.
 *
 * THE CLASSES ARE FORMFIELD'S OWN, COPIED VERBATIM from its input/textarea branches
 * (FormField.tsx:99-124) so a read-back row is pixel-identical to the editable row it
 * mirrors: same height token per size, same radius, same faint border, same 11px/300
 * ink, same padding. Only three things differ, and each is because this is not an
 * input: no focus ring (nothing to focus), `whitespace-pre-wrap` on the multi-line
 * sizes so a typed newline reads back as a newline, and `overflow-y-auto` so a long
 * description scrolls inside its measured box rather than growing it — the box height
 * is derived geometry, not a suggestion.
 *
 * EMPTY VALUES. A blank value renders the em dash, not an empty box: on a review
 * screen an empty box reads as "nothing was asked for" when it means "you left this
 * out". The dash is the same convention vmAdapters uses for an absent identity row.
 */

/** The three measured box heights, the same map FormField keeps (its HEIGHT const). */
const HEIGHT = {
  default: 'h-field',
  tall: 'h-field-tall',
  action: 'h-field-action',
} as const;

export interface ReadbackFieldProps {
  /** The value the builder entered. Blank/whitespace renders as an em dash. */
  value: string;
  /** Matches FormField's `size` so the read-back box is the height it mirrors. */
  size?: keyof typeof HEIGHT;
}

export function ReadbackField({ value, size = 'default' }: ReadbackFieldProps) {
  const multiline = size === 'tall' || size === 'action';
  const shown = value.trim() ? value : '—';

  return (
    <div
      className={[
        HEIGHT[size],
        'w-full min-w-0 rounded-md border border-border-subtle bg-transparent',
        multiline
          ? 'overflow-y-auto whitespace-pre-wrap px-field-x py-field-x'
          : 'flex items-center overflow-hidden px-field-x',
        'text-xs font-light text-ink',
      ].join(' ')}
    >
      {/* truncate on the single-line box so a long value clips at the measured
          width instead of widening the grid track it sits in. */}
      <span className={multiline ? '' : 'truncate'}>{shown}</span>
    </div>
  );
}
